import React from 'react';
import { getTablesNewFormat } from 'src/pages/OfferPage/TableSvgOrganizer/tableSvgOrganizerUtils';
import { TableFloor } from 'src/pages/OfferPage/TableSvgOrganizer/TableFloor';
import { NonRoomTable } from 'src/pages/OfferPage/TableSvgOrganizer/NonRoomTable';
import { PlannerGlobals } from 'src/components/Planner/planner/planner-globals';
import { ProjectQuoteUserProperties } from 'src/model/api/project.model';
import { GetProjectTreeProjectChildNode } from 'src/model/api/get-project-tree-project-childnode.model';
import { ProjectQuoteFloorVisibility } from 'src/api/project-quote-ui-slice';
import { GetAllManufacturerDiscountForProjectResponse } from "src/model/api/manufacturer-discount.model";
import { Product } from "src/model/product.model";
import { ManufacturerRecommendationResponse } from "src/model/api/manufacturer-recommendation";

// Finalizes a cloned floor SVG for the offer document: strips ids (keeping the
// floor_g reference target and gradients/masks), applies the quote view-box and
// caches the rendered React node. Ported from legacy DOCU_HELPER.processFloorSVG.
function processFloorSVG(svg: any) {
  const floorSVG = svg.plannerSVG;
  floorSVG.removeAttr("id");
  floorSVG
      .find("*")
      .not(".floor_g")
      .not("radialGradient")
      .not("mask")
      .removeAttr("id");
  floorSVG.addClass("quote-floorSVG");
  floorSVG.addClass("lightshadow");
  floorSVG.css("border", "");
  const cache: any = PlannerGlobals.quoteViewBoxCache || {};
  const vb = cache[svg.floorId];
  if (vb && !svg.isWiring) {
    floorSVG.attr("viewBox", vb);
  }

  floorSVG.css({ maxHeight: "800px", width: "auto", display: "block", margin: "0 auto" });
  svg.cachedFloor = (
      <div dangerouslySetInnerHTML={{ __html: floorSVG[0].outerHTML }}></div>
  );
  svg.floorHTML = floorSVG[0].outerHTML;
}

function processRoomSVG(svg: any) {
  const roomSVG = svg.roomSVG;
  roomSVG.removeAttr("id");
  roomSVG.find("*").removeAttr("id");
  roomSVG.addClass("quote-roomSVG");
  roomSVG.addClass("lightshadow");
  roomSVG.css("border", "");
  svg.cachedRoom = (
      <div dangerouslySetInnerHTML={{ __html: roomSVG[0].outerHTML }}></div>
  );
}

export const TableSvgOrganizer = (
  {
    projectId,
    cameraEnabled,
    tablesEnabled,
    detailsEnabled,
    wifiEnabled,
    wiringEnabled,
    roomMapsEnabled,
    floorMapsEnabled,
    lightingEnabled,
    lightingCalcs,
    tables,
    svgs,
    tableDLStarted,
    svgDLStarted,
    dispatchDownloadStates,
    currentRowInEditing,
    setCurrentRowInEditing,
    projectTreeNodes,
    measurementSystem,
    quoteUserProperties,
    floorVisibilities,
    allManufacturerDiscountForProject,
    manufacturerRecommendationForProject,
    customItemsForRecommendation,
  }: {
      projectId: number
      cameraEnabled: boolean
      tablesEnabled: boolean
      detailsEnabled: boolean
      wifiEnabled: boolean
      wiringEnabled: boolean
      roomMapsEnabled: boolean
      floorMapsEnabled: boolean
      lightingEnabled: boolean
      lightingCalcs: any
      tables: any
      svgs: any
      tableDLStarted: boolean
      svgDLStarted: boolean
      dispatchDownloadStates: any
      currentRowInEditing: any
      setCurrentRowInEditing: any
      projectTreeNodes: GetProjectTreeProjectChildNode[]
      measurementSystem: string
      quoteUserProperties: ProjectQuoteUserProperties,
      floorVisibilities: ProjectQuoteFloorVisibility[],
      allManufacturerDiscountForProject: GetAllManufacturerDiscountForProjectResponse,
      manufacturerRecommendationForProject: ManufacturerRecommendationResponse,
      customItemsForRecommendation: Product[],
    }
) => {

  const { tableFloors, nonRoomTables } = React.useMemo(() => {
    if (tables && svgs && svgs.plannerSVGs && svgs.roomSVGs) {
      svgs.plannerSVGs.forEach((svg: any) => processFloorSVG(svg));
      svgs.roomSVGs.forEach((svg: any) => processRoomSVG(svg));
    }
    return getTablesNewFormat(
        tables,
        svgs,
        lightingCalcs,
        projectTreeNodes,
        quoteUserProperties
    );
  }, [tables, svgs, lightingCalcs, quoteUserProperties]);

  return (
    <>
      {tableFloors
        ? tableFloors.map((tableFloor) => (
            <TableFloor
              projectId={projectId}
              key={tableFloor.id}
              tableFloor={tableFloor}
              detailsEnabled={detailsEnabled}
              tablesEnabled={tablesEnabled}
              tableDLStarted={tableDLStarted}
              svgDLStarted={svgDLStarted}
              wifiEnabled={wifiEnabled}
              wiringEnabled={wiringEnabled}
              cameraEnabled={cameraEnabled}
              floorMapsEnabled={
                floorVisibilities.find((floorVisibility) => floorVisibility.floorId === Number(tableFloor.id))?.floorPlanVisible ?? true
              }
              roomMapsEnabled={
                  floorVisibilities.find((floorVisibility) => floorVisibility.floorId === Number(tableFloor.id))?.roomFloorPlansVisible ?? true
              }
              lightingEnabled={lightingEnabled}
              svgs={svgs}
              currency={quoteUserProperties.selected_currency}
              dispatchDownloadStates={dispatchDownloadStates}
              currentRowInEditing={currentRowInEditing}
              setCurrentRowInEditing={setCurrentRowInEditing}
              measurementSystem={measurementSystem}
              quoteUserProperties={quoteUserProperties}
              allManufacturerDiscountForProject={allManufacturerDiscountForProject}
              manufacturerRecommendationForProject={manufacturerRecommendationForProject}
              customItemsForRecommendation={customItemsForRecommendation}
            />
          ))
        : null}
      {tablesEnabled
        ? nonRoomTables
            .filter(
              (table) =>
                table.entitytype !== "projectcustomtable" &&
                table.entitytype !== "sectiontable"
            )
            .map((table) => (
              <NonRoomTable
                projectId={projectId}
                key={`${table.entitytype}_${table.id}`}
                tablesEnabled={tablesEnabled}
                table={table}
                roomMapsEnabled={roomMapsEnabled}
                detailsEnabled={detailsEnabled}
                dlStarted={tableDLStarted}
                infoButton={false}
                currency={quoteUserProperties.selected_currency}
                dispatchDownloadStates={dispatchDownloadStates}
                currentRowInEditing={currentRowInEditing}
                setCurrentRowInEditing={setCurrentRowInEditing}
                labourCostEnabled={false}
                labourCost={null}
                measurementSystem={measurementSystem}
                quoteUserProperties={quoteUserProperties}
                allManufacturerDiscountForProject={allManufacturerDiscountForProject}
                manufacturerRecommendationForProject={manufacturerRecommendationForProject}
                customItemsForRecommendation={customItemsForRecommendation}
              />
            ))
        : null}
    </>
  );
};
